import type { AxisSelection } from '@atlas/types'
import { CardShell, EmptyState, ListSkeleton } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader } from '@/components/editorial/PageHeader'
import { RuleCard } from '@/components/rules/RuleCard'
import { type RuleFacetGroup, RuleFilterRail } from '@/components/rules/RuleFilterRail'
import type { Facet } from '@/domain/library.types'
import { type Rule, scopeLabel } from '@/domain/rules.types'
import { type RailValue, railAxesToFacets, useAxesWithValues, useFacetedOptions } from '@/hooks/useLibrary'
import { useRules } from '@/hooks/useRules'
import { useCan } from '@/lib/capabilities'
import { REFUSAL, Restricted } from '@/lib/refusal'

/** Active values per axis (multi-select: a rule matches an axis if its value is any of these). */
type Selected = Record<string, string[]>
interface Scoped {
	rule: Rule
	scope: string
}

interface FacetDef {
	axis: string
	label: string
}

/** Built-in facets, always shown first (design §8.13). */
const BASE_FACET_DEFS: readonly FacetDef[] = [
	{ axis: 'type', label: 'Type' },
	{ axis: 'severity', label: 'Severity' },
	{ axis: 'scope', label: 'Scope' },
] as const

const BASE_AXES = new Set(BASE_FACET_DEFS.map((d) => d.axis))

function buildValueLabels(facets: Facet[]): Record<string, string> {
	const m: Record<string, string> = {}
	for (const f of facets) for (const v of f.values) m[v.code] = v.label
	return m
}

/**
 * label→code per axis (the inverse of a facet's own `values`, scoped per axis
 * since a code is only guaranteed unique WITHIN its axis, not globally). Needed
 * because this page's selection state (`Selected`) is label-space throughout
 * (`axisValues()` returns labels, matched against `sel`) while `/facet/batch`
 * is code-space — the bridge lives here, at the boundary, not by re-plumbing
 * the rest of the page.
 */
function labelToCodeByAxis(facets: Facet[]): Map<string, Map<string, string>> {
	const byAxis = new Map<string, Map<string, string>>()
	for (const f of facets) byAxis.set(f.axis, new Map(f.values.map((v) => [v.label, v.code])))
	return byAxis
}

/**
 * The value(s) a rule carries on an axis. Built-in axes are single-valued; any
 * registry axis added via "＋ Add variable" reads the rule's classification tuple
 * directly — multi-value, and an untouched axis means "applies to all" (DEC-16), so
 * it returns no concrete values (matched unconditionally, never listed as an option).
 */
function axisValues(s: Scoped, axis: string, valueLabels: Record<string, string>): string[] {
	if (axis === 'type') return [s.rule.type]
	if (axis === 'severity') return [s.rule.severity]
	if (axis === 'scope') return [s.scope]
	const codes = s.rule.classification[axis] ?? []
	return codes.map((c) => valueLabels[c] ?? c)
}

/** A rule matches the free-text search independently of any facet selection. */
function matchesSearch(s: Scoped, q: string): boolean {
	return !q || `${s.rule.statement} ${s.rule.id} ${s.rule.type}`.toLowerCase().includes(q)
}

/**
 * A rule passes every active facet except `skip` (multi-select: OR within an axis,
 * AND across axes; unclassified on a registry axis = applies to all, DEC-16).
 * Deliberately independent of the search text — the facet rail's available options
 * are driven only by other facets, not by what's typed in search.
 */
function matchesFacets(
	s: Scoped,
	sel: Selected,
	defs: readonly FacetDef[],
	valueLabels: Record<string, string>,
	skip?: string,
): boolean {
	return defs.every((d) => {
		if (skip === d.axis) return true
		const active = sel[d.axis] ?? []
		if (active.length === 0) return true
		const vals = axisValues(s, d.axis, valueLabels)
		return vals.length === 0 || vals.some((v) => active.includes(v))
	})
}

const uniq = (xs: string[]) => Array.from(new Set(xs))

/**
 * Facet-rail groups: base facets (type/severity/scope) keep their existing
 * client-side, cross-filtered reachability; registry ("＋ Add variable")
 * facets get their reachable values from the shared faceted-options hook.
 * Either way, a value the user has already selected always stays
 * visible/selectable even if it would currently combine with other active
 * facets to zero rows — selecting a filter must never make an existing
 * selection disappear out from under you.
 */
function buildFacetGroups(
	facetDefs: readonly FacetDef[],
	scoped: Scoped[],
	sel: Selected,
	valueLabels: Record<string, string>,
	extraAxisOptions: Record<string, RailValue[]>,
): RuleFacetGroup[] {
	return facetDefs.map((d) => {
		if (BASE_AXES.has(d.axis)) {
			const reachable = scoped
				.filter((s) => matchesFacets(s, sel, facetDefs, valueLabels, d.axis))
				.flatMap((s) => axisValues(s, d.axis, valueLabels))
			return { axis: d.axis, label: d.label, values: uniq([...reachable, ...(sel[d.axis] ?? [])]), removable: false }
		}
		const options = (extraAxisOptions[d.axis] ?? []).map((o) => o.label)
		return { axis: d.axis, label: d.label, values: uniq([...options, ...(sel[d.axis] ?? [])]), removable: true }
	})
}

function toggleValue(values: string[], value: string): string[] {
	return values.includes(value) ? values.filter((v) => v !== value) : [...values, value]
}

function RuleResults({
	isLoading,
	filtered,
	onOpen,
}: {
	isLoading: boolean
	filtered: Scoped[]
	onOpen: (id: string) => void
}) {
	if (isLoading) return <ListSkeleton label="Loading…" />
	if (filtered.length === 0)
		return (
			<CardShell className="border-dashed bg-transparent p-8 text-center">
				<EmptyState title="No rules match the current filters." variant="filtered" />
			</CardShell>
		)
	return (
		<div className="space-y-2.5">
			{filtered.map((s) => (
				<RuleCard key={s.rule.id} rule={s.rule} scope={s.scope} onOpen={() => onOpen(s.rule.id)} />
			))}
		</div>
	)
}

/** Rules Explorer (design §8.13): search + left facet rail (cross-filtering) + rule cards. */
export function RulesPage() {
	const navigate = useNavigate()
	const { canConfigure } = useCan()
	const { data: rules = [], isLoading } = useRules()
	const { axes } = useAxesWithValues()
	const facets = useMemo(() => railAxesToFacets(axes), [axes])
	const [query, setQuery] = useState('')
	const [sel, setSel] = useState<Selected>({})
	const [extraAxes, setExtraAxes] = useState<string[]>([])

	const valueLabels = useMemo(() => buildValueLabels(facets), [facets])
	const labelToCode = useMemo(() => labelToCodeByAxis(facets), [facets])
	const scoped = useMemo<Scoped[]>(
		() => rules.map((r) => ({ rule: r, scope: scopeLabel(r.classification, valueLabels) })),
		[rules, valueLabels],
	)

	// "＋ Add variable" (design §8.13): any registry axis not already a facet group.
	const facetDefs: FacetDef[] = [
		...BASE_FACET_DEFS,
		...extraAxes.map((axis) => ({ axis, label: facets.find((f) => f.axis === axis)?.label ?? axis })),
	]
	const availableAxes = facets.filter((f) => !BASE_AXES.has(f.axis) && !extraAxes.includes(f.axis))

	const q = query.trim().toLowerCase()
	const filtered = scoped.filter((s) => matchesSearch(s, q) && matchesFacets(s, sel, facetDefs, valueLabels))

	// Registry-axis ("＋ Add variable") facet options are cross-filtered (skip-self)
	// server-side via the shared batch-facet hook — `sel` is label-space (this
	// page's own state) but the backend is code-space, so it's bridged at this
	// boundary via `labelToCode`. `type`/`severity`/`scope` are rule fields, not
	// classification axes, so the facet engine has no notion of them — when any
	// is active, the rule ids they narrow to are passed as `candidateObjectIds`,
	// the one hook the engine gives us for a constraint it can't express itself,
	// so a base-facet selection still narrows registry-axis options too.
	const baseFacetsActive = BASE_FACET_DEFS.some((d) => (sel[d.axis] ?? []).length > 0)
	const candidateObjectIds = baseFacetsActive
		? scoped.filter((s) => matchesFacets(s, sel, BASE_FACET_DEFS, valueLabels)).map((s) => s.rule.id)
		: undefined
	const extraAxisSelection: AxisSelection = Object.fromEntries(
		extraAxes.map((axis) => {
			const codeOf = labelToCode.get(axis)
			return [axis, (sel[axis] ?? []).map((label) => codeOf?.get(label) ?? label)]
		}),
	)
	const { optionsByAxis: extraAxisOptions } = useFacetedOptions(extraAxisSelection, extraAxes, { candidateObjectIds })

	const groups = buildFacetGroups(facetDefs, scoped, sel, valueLabels, extraAxisOptions)
	const toggle = (axis: string, value: string) => setSel((s) => ({ ...s, [axis]: toggleValue(s[axis] ?? [], value) }))
	const addAxis = (axis: string) => setExtraAxes((xs) => uniq([...xs, axis]))
	const removeAxis = (axis: string) => {
		setExtraAxes((xs) => xs.filter((x) => x !== axis))
		setSel((s) => {
			const next = { ...s }
			delete next[axis]
			return next
		})
	}

	return (
		<div>
			<PageHeader
				eyebrow="Rules explorer"
				title="Which rules apply?"
				actions={
					<Restricted allowed={canConfigure} why={REFUSAL.writeRule}>
						<Button
							className="rounded-ds-full px-4"
							onPress={() => {
								void navigate('/rules/new')
							}}
						>
							＋ New rule
						</Button>
					</Restricted>
				}
			/>
			<div className="mb-5 max-w-md">
				<input
					aria-label="Search rules"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					placeholder="Search rules by text, id or type…"
					className="h-10 w-full rounded-ds-md border border-ds-border bg-ds-surface-elevated px-3 text-sm text-ds-text"
				/>
			</div>
			<div className="grid grid-cols-1 gap-5 lg:grid-cols-[220px_1fr]">
				<RuleFilterRail
					groups={groups}
					selected={sel}
					onToggle={toggle}
					availableAxes={availableAxes.map((f) => ({ axis: f.axis, label: f.label }))}
					onAddAxis={addAxis}
					onRemoveAxis={removeAxis}
				/>
				<div>
					<p className="mb-3 font-mono text-xs text-ds-text-subtle">{filtered.length} rules</p>
					<RuleResults
						isLoading={isLoading}
						filtered={filtered}
						onOpen={(id) => {
							void navigate(`/rules/${id}`)
						}}
					/>
				</div>
			</div>
		</div>
	)
}
